import { useMemo, useState } from "react"
import { useIntl } from "react-intl"
import { useQuery } from "react-query"
import { useNavigate, useParams } from "react-router-dom"

import { Button, ICONS, StaffHeading4 } from "@doktor-se/bones-ui"
import { Loader } from "@doktor-se/bones-ui/dist/web-shared/components"

import { fetchAssignedConversations, fetchClinicConversations, fetchConversationById, fetchPatientById } from "api"
import { handleFlagged } from "api/error/handler"
import { assignToOtherStaffEnabled, reopenConversationEnabled } from "config"
import { useAppDispatch, useAppSelector } from "lib/hooks"
import { removeConversation } from "reducers/conversations/conversations.reducer"
import { setConversation } from "reducers/selected"

import ConversationDetails from "./components/ConversationDetails"
import ConversationFeed from "./components/ConversationFeed"
import ConfirmReportSent from "components/topmenu/ReportBug/ConfirmReportSent"
import ReportBugDialog from "components/topmenu/ReportBug/ReportBugDialog"
import ReassignDialog from "components/v2/conversationList/components/ReassignDialog/ReassignDialog"

import styles from "./Errand.module.scss"

const Errand = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { conversationId } = useParams()
  const intl = useIntl()
  const [showReopenDialog, setShowReopenDialog] = useState(false)
  const [showReassignDialog, setShowReassignDialog] = useState(false)
  const [showReportDialog, setShowReportDialog] = useState(false)
  const [showFlagSentModal, setShowFlagSentModal] = useState(false)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const defaultRole = useAppSelector(state => state.auth.defaultRole)
  const currentUserId = useAppSelector(state => state.auth.user?.id)

  const features = useMemo(
    () => ({
      hasAssignToOtherStaff: assignToOtherStaffEnabled(defaultRole),
      hasReopen: reopenConversationEnabled(defaultRole)
    }),
    [defaultRole]
  )

  const handleReassignSuccess = async () => {
    // Load the conversation into Redux store first, then set it as selected and navigate
    try {
      setIsRedirecting(true)
      const updatedConversation = await dispatch(fetchConversationById(conversationId as string))

      // Only remove from current user's list if reassigned to someone else
      if (updatedConversation && updatedConversation.assignedStaffId !== currentUserId) {
        dispatch(removeConversation(conversationId as string))
      }

      // Refresh both assigned conversations and clinic conversations to update all errands lists
      await Promise.all([dispatch(fetchAssignedConversations()), dispatch(fetchClinicConversations(false))])
      dispatch(setConversation(conversationId as string))
      navigate("/")
    } catch (error) {
      console.error("Failed to load conversation:", error)
      setIsRedirecting(false)
    }
  }

  const {
    isLoading: isConvLoading,
    data: conv,
    isSuccess
  } = useQuery({
    queryKey: conversationId,
    queryFn: () => dispatch(fetchConversationById(conversationId as string))
  })
  const { isLoading: isPatLoading, data: pat } = useQuery({
    queryKey: conv?.patientId,
    queryFn: () => dispatch(fetchPatientById(conv!.patientId)),
    enabled: isSuccess
  })

  const profile = pat?.profiles?.find(p => p.id === conv?.profileId)

  if (isConvLoading || isPatLoading || isRedirecting) return <Loader type="full" />

  return (
    <div className={styles.errandContainer}>
      {conv && pat ? (
        <>
          <div>
            <Button
              type="button"
              Icon={<ICONS.ChevronLeft />}
              onPress={() => navigate(-1)}
              color={"primary"}
              variant={"outline"}>
              {intl.formatMessage({ id: "label.back" })}
            </Button>
            <ConversationDetails
              conversation={conv}
              patient={pat}
              profile={profile}
              features={features}
              showReopenDialog={() => setShowReopenDialog(true)}
              showReassignDialog={() => setShowReassignDialog(true)}
              showReportDialog={() => setShowReportDialog(true)}
            />
          </div>
          <ConversationFeed conversation={conv} profile={profile} patient={pat} />

          <ReassignDialog
            isOpen={showReopenDialog}
            conversation={conv}
            onClose={() => setShowReopenDialog(false)}
            header={intl.formatMessage({ id: "label.errand.reopenTo" })}
            onSuccess={handleReassignSuccess}
          />
          <ReassignDialog
            isOpen={showReassignDialog}
            conversation={conv}
            onClose={() => setShowReassignDialog(false)}
            onSuccess={handleReassignSuccess}
          />
          <ReportBugDialog
            isOpen={showReportDialog}
            onClose={() => setShowReportDialog(false)}
            flag={(flagBody, flagSubject) => {
              dispatch(handleFlagged(flagBody, flagSubject, conversationId))
              setShowReportDialog(false)
              setShowFlagSentModal(true)
            }}
          />
          <ConfirmReportSent isOpen={showFlagSentModal} onClose={() => setShowFlagSentModal(false)} />
        </>
      ) : (
        <StaffHeading4>{intl.formatMessage({ id: "networktest.error.unknown" })}</StaffHeading4>
      )}
    </div>
  )
}

export default Errand
